import {parseAppZoneDate} from '@/utils/dateUtils'

/**
 * EVERY PICKUP TIME THIS RIDE HAS HAD, oldest first, the current one last.
 *
 * The pickup moves after booking — a passenger's delay pushes it (`requested_delays[]`
 * with `pickup_shifted: true` carries `scheduled_at_before` → `scheduled_at_after`), and
 * flight tracking can shift it too (`original_scheduled_at` is the booked time then).
 * Screens show the earlier ones struck through ahead of the current one, so a driver or
 * passenger who remembers "11:50" can see why it now says 12:46.
 *
 * Raw server strings (Toronto wall clock) are returned untouched — each screen formats them
 * with the formatter it already uses. Consecutive duplicates are dropped, compared to the
 * minute, so a delay that did not move the pickup adds nothing.
 *
 * @returns {string[]} at least `[trip.scheduled_at]` when the trip has one, else []
 */
export function pickupTimeHistory(trip) {
    if (!trip?.scheduled_at) return []

    const raw = []
    if (trip.original_scheduled_at) raw.push(trip.original_scheduled_at)

    const shifts = (Array.isArray(trip.requested_delays) ? trip.requested_delays : [])
        .filter((d) => d?.pickup_shifted && d.scheduled_at_before && d.scheduled_at_after)
        .sort((a, b) => (parseAppZoneDate(a.requested_at)?.getTime() || 0) - (parseAppZoneDate(b.requested_at)?.getTime() || 0))
    for (const d of shifts) raw.push(d.scheduled_at_before, d.scheduled_at_after)

    raw.push(trip.scheduled_at)

    const minuteOf = (value) => {
        const ms = parseAppZoneDate(value)?.getTime()
        return Number.isFinite(ms) ? Math.floor(ms / 60000) : String(value)
    }

    const out = []
    for (const value of raw) {
        if (out.length && minuteOf(out[out.length - 1]) === minuteOf(value)) continue
        out.push(value)
    }
    // The current time is always last, even if an earlier entry repeats it.
    const current = out[out.length - 1]
    return [...out.slice(0, -1).filter((v) => minuteOf(v) !== minuteOf(current)), current]
}

/**
 * The pickup clock's `schedule` for `WaitingTimer` — the trip's pickup time and the pickup
 * free minutes from its waiting policy — or null when either is missing or waiting fees are
 * off. Used until the server's waiting record exists (the driver has not marked arrival).
 */
export function pickupWaitSchedule(trip) {
    const policy = trip?.policy_popup?.waiting || trip?.waiting || null
    if (!trip?.scheduled_at || !policy || policy.enabled === false) return null
    const minutes = Number(policy.pickup?.free_minutes)
    return Number.isFinite(minutes) ? {startAt: trip.scheduled_at, freeSeconds: minutes * 60} : null
}

export default pickupTimeHistory
